import { ApiClient } from './api.ts';

export interface Identity {
    id: string;
    organization_id: string;
    primary_alias: string;
    alias_type: string;
    external_id?: string;
    display_name?: string;
    created_at: string;
    updated_at: string;
}

export interface Alias {
    id: string;
    identity_id: string;
    alias: string;
    alias_type: string;
    is_primary: boolean;
    display_name?: string;
    created_at: string;
}

export interface Credential {
    type?: string;
    credential_id: string;
    device_type?: string;
    rp_id?: string;
    sign_count?: number;
    created_at: string;
    last_used_at?: string;
}

export interface IdentitySettings {
    organization_id: string;
    allow_public_registration: boolean;
    allow_magic_links: boolean;
    allow_passwords: boolean;
    created_at: string;
    updated_at: string;
}

class IdentityService {
    private static instance: IdentityService;
    private api = ApiClient.getInstance();

    private constructor() {}

    public static getInstance(): IdentityService {
        if (!IdentityService.instance) {
            IdentityService.instance = new IdentityService();
        }
        return IdentityService.instance;
    }

    private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        try {
            const res = await this.api.request(`/organization${endpoint}`, options);
            return res as T;
        } catch (error: any) {
            console.error(`Identity API Error: ${error.message}`);
            throw error;
        }
    }

    // Identities
    public async listIdentities(orgId: string): Promise<Identity[]> {
        return this.request<Identity[]>(`/${orgId}/identity`);
    }

    public async getIdentity(orgId: string, id: string): Promise<Identity> {
        return this.request<Identity>(`/${orgId}/identity/${id}`);
    }

    // Aliases
    public async listAliases(orgId: string, identityId: string): Promise<Alias[]> {
        return this.request<Alias[]>(`/${orgId}/identity/${identityId}/aliases`);
    }

    // Credentials
    public async listCredentials(orgId: string, identityId: string): Promise<Credential[]> {
        return this.request<Credential[]>(`/${orgId}/identity/${identityId}/credentials`);
    }

    public async removeCredential(orgId: string, identityId: string, credentialId: string): Promise<void> {
        await this.request<void>(`/${orgId}/identity/${identityId}/credentials/${credentialId}`, {
            method: 'DELETE',
        });
    }

    // Settings
    public async getSettings(orgId: string): Promise<IdentitySettings> {
        return this.request<IdentitySettings>(`/${orgId}/settings/identity`); // Assumes we will add an endpoint for identity settings or fetch it from org config
    }

    public async updateSettings(orgId: string, settings: Partial<IdentitySettings>): Promise<IdentitySettings> {
        return this.request<IdentitySettings>(`/${orgId}/settings/identity`, {
            method: 'PUT',
            body: JSON.stringify(settings),
        });
    }
}

export const identityService = IdentityService.getInstance();
